import { html, svg, property } from 'lit-element';
import { nothing } from 'lit-html';
import { ifDefined } from 'lit-html/directives/if-defined';
import { nextUniqueId } from '../../shared/base/unique-id';
import {
  KatLitElement,
  Keys,
  register,
  event,
  EventEmitter,
} from '../../shared/base';
import { getFirstMatchingParent, rangeMap } from '../../shared/utils';
import baseStyles from '../../shared/base/base.lit.scss';
import formItemStyles from '../../shared/base/form-item.base.lit.scss';
import { roundStars } from './star-rating-calculator';
import styles from './star-rating.lit.scss';

const FULL_STAR = 'full';
const HALF_STAR = 'half';
const EMPTY_STAR = 'empty';

const DEFAULT_STARS_VALUE = 5;
const DEFAULT_RATING_VALUE = 0;

/**
 * @component {kat-star-rating} Star Rating is a common Amazon affordance that allows customers to rate their experience.
 * @guideline Do Star ratings can be filled in half star or full star increments.
 * @guideline Dont Do not use gradients or change the fill colors
 * @guideline Do Do use the input variant to allow users to select a rating.
 * @guideline Do Do use the default variant to display a rating.
 * @guideline Dont StarRating and StarRatingWithLink variants are to be used for display purposes only. StarRatingInput and StarRatingInputWithError are to be used only as inputs (not for display)
 * @example StarRating {"value": "4.5","review":"236"}
 * @example StarRatingWithLink {"value": "2","review":"236","href":"https://www.amazon.com"}
 * @example StarRatingInput {"variant": "input", "name": "starExample", "label": "Rate this product"}
 * @example StarRatingInputWithError {"value": "2","variant": "input", "label": "Rate this product", "error-label": "Please choose a rating", "name": "starErrorExample"}
 * @status Production
 * @theme flo
 */
@register('kat-star-rating')
export class KatStarRating extends KatLitElement {
  /**
   * The score that will be displayed as full stars and half-stars out of the total number of stars.
   * @required
   * @type {string | number}
   */
  @property()
  get value(): number {
    return this._value;
  }

  set value(value: number) {
    this._processNumericUpdates(value, 'value', DEFAULT_RATING_VALUE);
  }

  /** The star rating label */
  @property()
  label?: string;

  /** The review text */
  @property()
  review?: string;

  /** The text to show if there is an error. */
  @property({ attribute: 'error-label' })
  errorLabel?: string;

  /** This will make the star rating component a clickable link */
  @property()
  href?: string;

  /**
   * The number of stars to display. Defaults to 5.
   * @type {string | number}
   */
  @property()
  get stars(): number {
    return this._stars;
  }

  set stars(value: number) {
    this._processNumericUpdates(value, 'stars', DEFAULT_STARS_VALUE);
  }

  /**
   * Defines if the component can be used to display or set a rating.
   * @enum {value} default Component is display only.
   * @enum {value} input Component is used to select star rating.
   */
  @property()
  variant?: string;

  /** The name of this as an input inside a form. */
  @property()
  name?: string;

  /**
   * @deprecated
   * These will both be removed in 1.3 in favor of requiring these values to
   * be set as numbers.
   */
  _value = 0;
  _stars = 5;

  _propsToReflectToHiddenInput = ['name', 'value'];
  _conditionsToRenderHiddenInput = () => !!this.name;
  _extraLogicForHiddenInput = input => {
    input.setAttribute('part', 'star-rating-input-hidden');
  };

  /** Fires when the user selects a different rating. */
  @event('change', true)
  private _change: EventEmitter<{ value: number }>;

  static get styles() {
    return [baseStyles, formItemStyles, styles];
  }

  constructor() {
    super();
    this.uniqueId = this.uniqueId || nextUniqueId();
    this._uniqueGradientId = this.uniqueId;
  }

  private _processNumericUpdates(value, prop, defaultValue) {
    const privateProp = `_${prop}`;
    const oldValue = this[privateProp];

    let newValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(newValue)) {
      newValue = defaultValue;
    }

    this[privateProp] = newValue;
    this.requestUpdate(prop, oldValue);
  }

  private async _handleEvent(event) {
    if (this.variant === 'input') {
      const value = this._getRatingFromStarEvent(event);
      if (value != null && value !== this.value) {
        this.value = value;
        await this.updateComplete;
        this._change.emit({ value });
      }
    }
  }

  private _onClick(event) {
    this._handleEvent(event);
  }

  private _onKeyPress(event) {
    if (Keys.Confirm.includes(event.keyCode)) {
      this._handleEvent(event);
    }
  }

  createRenderRoot() {
    return this.attachShadow({ mode: 'open', delegatesFocus: true });
  }

  render() {
    const starsElement = this._createStarsElement();
    const review = html`
      <span class="review" part="star-rating-review" tabindex="0"
        >${this.review}</span
      >
    `;

    const label = this.label
      ? html`
          <kat-label
            class="form-label"
            part="star-rating-label"
            for=${this.uniqueId}
            text=${this.label}
          ></kat-label>
        `
      : nothing;

    const firstLine = (() => {
      const ariaLabel =
        this.variant !== 'input'
          ? `${this.value} star${this.value === 1 ? '' : 's'}`
          : undefined;

      const getFirstLine = children => {
        const tabIndex = this.variant !== 'input' ? '0' : undefined;
        return html`
          <div
            class="wrapper"
            aria-label="${ifDefined(ariaLabel)}"
            tabindex="${ifDefined(tabIndex)}"
          >
            ${children}
          </div>
        `;
      };

      const getLink = children => html`
        <a
          part="star-rating-link"
          class="wrapper link"
          href="${this.href}"
          aria-label="${ifDefined(ariaLabel)}"
          target="_blank"
          >${children}</a
        >
      `;

      if (this.href) {
        if (this.variant === 'input') {
          return getFirstLine([starsElement, getLink(review)]);
        }
        return getLink([starsElement, review]);
      }
      return getFirstLine([starsElement, review]);
    })();

    const error = this.errorLabel
      ? html`
          <div class="metadata">
            <kat-label
              class="label--error"
              variant="constraint"
              text=${this.errorLabel}
              state="error"
              for=${this.uniqueId}
              part="star-rating-label-error"
            ></kat-label>
          </div>
        `
      : nothing;

    return html`
      ${label}${firstLine}${error}<slot name="private-light-dom"></slot>
    `;
  }

  _createStarsElement() {
    const numStars = this.stars;
    const [fullStarCount, hasHalfStar] = roundStars(this.value);

    const icons = rangeMap(numStars, i => {
      const starType =
        i < fullStarCount
          ? FULL_STAR
          : hasHalfStar && i === fullStarCount
          ? HALF_STAR
          : EMPTY_STAR;

      return this._createStar(starType, i);
    });

    return html`
      <div
        class="stars"
        part="stars"
        @click=${this._onClick}
        @keypress=${this._onKeyPress}
      >
        ${icons}
      </div>
    `;
  }

  _createGradientDefinition() {
    return svg`
      <defs>
        <linearGradient
          x1="50%"
          y1="0%"
          x2="50%"
          y2="100%"
          id="${this._uniqueGradientId}"
        >
          <stop offset="0%" stop-color="#FED007" />
          <stop offset="100%" stop-color="#FEB502" />
        </linearGradient>
      </defs>
    `;
  }

  _createStar(type, index) {
    const gradientFill = `url(#${this._uniqueGradientId})`;
    const halfPolygon = svg`
      <polygon
        class="polygon__partial"
        points="12,13.8 8.6,15.7 9.3,11.9 6.6,9.2 10.3,8.7 12,5.3"
        fill="${gradientFill}"
      />
    `;

    const children = [
      type === EMPTY_STAR ? nothing : this._createGradientDefinition(),
      svg`
        <g>
          <path
            d="M16,16.6l-0.8-4.5l3.3-3.2L14,8.2l-2-4.1l-2,4.1L5.5,8.9l3.3,3.2L8,16.6l4-2.1L16,16.6z"
            fill="${type === FULL_STAR ? gradientFill : '#FFFFFF'}"
          />
          ${type === HALF_STAR ? halfPolygon : nothing}
        </g>
      `,
    ];

    const tabindex = this.variant === 'input' ? '0' : undefined;
    const ariaLabel =
      this.variant === 'input'
        ? `${index + 1} star${index === 0 ? '' : 's'}`
        : undefined;
    return html`
      <div
        class="star"
        part="star"
        data-position="${index + 1}"
        tabindex=${ifDefined(tabindex)}
        aria-label="${ifDefined(ariaLabel)}"
        data-star-type="${type}"
      >
        <svg focusable="false" class="star__svg" viewBox="5 4 14 14">
          ${children}
        </svg>
      </div>
    `;
  }

  _getRatingFromStarEvent(evt) {
    const icon = getFirstMatchingParent(
      evt.target,
      el => el.dataset?.position,
      this
    );
    if (!icon) {
      return null;
    }
    return +icon.dataset.position;
  }
}
